/**
 * E5 §3's opt-in review/confirm screen (ACTION_PLAN.md E5). Rendered by
 * GenerateJob.tsx only when a review_inferred_schema=true call comes
 * back with a `review` result instead of a script — never a default
 * step in the flow.
 *
 * Only top-level fields are editable here — nested `children` are shown
 * read-only. Editing arbitrarily deep structure in a flat table is out
 * of proportion for v1; a caller who needs to correct a nested field can
 * still resubmit through the normal unresolved-fields fallback (§5,
 * FieldMapping.tsx) once the script comes back.
 */

import { useState } from 'react';

import type { CanonicalField, CanonicalSchemaShape, SchemaReviewResult } from '../api/client';

// E11 web-portal design spec §0 — exported for SchemaFieldListBuilder.tsx's
// own reuse, the same flat, top-level-only field-list posture this page
// already established.
export const CANONICAL_TYPES = ['string', 'integer', 'number', 'boolean', 'object', 'array'];

interface SchemaReviewProps {
  review: SchemaReviewResult['review'];
  busy: boolean;
  onConfirm: (source: CanonicalSchemaShape, destination: CanonicalSchemaShape) => void;
}

export function SchemaReview({ review, busy, onConfirm }: SchemaReviewProps) {
  const [source, setSource] = useState<CanonicalSchemaShape>(review.source);
  const [destination, setDestination] = useState<CanonicalSchemaShape>(review.destination);

  function updateField(side: 'source' | 'destination', index: number, patch: Partial<CanonicalField>) {
    const setter = side === 'source' ? setSource : setDestination;
    setter((prev) => ({
      ...prev,
      fields: prev.fields.map((field, i) => (i === index ? { ...field, ...patch } : field)),
    }));
  }

  function renderTable(label: string, schema: CanonicalSchemaShape, side: 'source' | 'destination') {
    return (
      <div className="schema-review-side">
        <h3>{label}</h3>
        <table className="schema-review-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Nullable</th>
            </tr>
          </thead>
          <tbody>
            {schema.fields.map((field, i) => (
              <tr key={field.name}>
                <td>{field.name}</td>
                <td>
                  <select
                    value={field.type}
                    onChange={(e) => updateField(side, i, { type: e.target.value })}
                  >
                    {CANONICAL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={field.nullable}
                    onChange={(e) => updateField(side, i, { nullable: e.target.checked })}
                    aria-label={`${field.name} nullable`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section className="schema-review">
      <h2>Review the inferred schema</h2>
      <p className="muted">
        Confirm the field names, types and nullability the inference step guessed before
        generating the script.
      </p>
      {renderTable('Source', source, 'source')}
      {renderTable('Destination', destination, 'destination')}
      <button
        className="btn btn--primary"
        disabled={busy}
        onClick={() => onConfirm(source, destination)}
      >
        {busy ? 'Generating…' : 'Confirm and generate'}
      </button>
    </section>
  );
}
